/**
 * Il Gantt di progetto.
 *
 * ## Revisione del 21-08-2026
 *
 * - **L'asse si calcola dai dati.** Era cablato `15 apr → 31 ott 2026`: dal 1°
 *   novembre la riga «oggi» sarebbe sparita e tutte le barre si sarebbero
 *   schiacciate contro il bordo destro, senza che nessun errore lo segnalasse.
 *   Ora la finestra abbraccia gli elementi **e** oggi, arrotondata al mese.
 * - **Le milestone sono linee verticali, disegnate una volta.** Prima erano
 *   ridisegnate *dentro ogni corsia*: il grafico aveva gli stessi tre pallini
 *   ripetuti su ogni riga, e sembrava che ogni modulo avesse le sue milestone.
 * - **Le etichette non si troncano più** a 180px («M2 Pianificazione A…»); la
 *   colonna si adatta, e a piena pagina c'è tutto lo spazio che serve.
 * - **Le barre senza modulo** sono le fasi libere (collaudo, formazione),
 *   possibili dalla migration 0063.
 *
 * Due modi di stare in pagina: `compatto` nella card della Panoramica (dove è
 * cliccabile e porta alla pagina intera) e disteso in `/progetto/gantt`, che è
 * anche la versione che si stampa.
 */

import { useMemo } from "react";

import { cn } from "@/lib/utils";
import type { GanttElemento, Modulo } from "@/types/api";

interface GanttProps {
  elementi: GanttElemento[];
  moduli: Modulo[];
  /** Data odierna — override per test e anteprime. */
  oggi?: Date;
  /** `compatto` sta dentro una card; `disteso` occupa la pagina e si stampa. */
  variante?: "compatto" | "disteso";
}

const MESI_IT = [
  "gen",
  "feb",
  "mar",
  "apr",
  "mag",
  "giu",
  "lug",
  "ago",
  "set",
  "ott",
  "nov",
  "dic",
];

function giorniTra(a: Date, b: Date): number {
  return (b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24);
}

function primoDelMese(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

function primoDelMeseDopo(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1));
}

/**
 * La finestra temporale: dal primo del mese più antico all'ultimo del mese più
 * recente, con **oggi sempre dentro** — un Gantt in cui non si vede dove siamo
 * non serve a niente.
 */
function calcolaFinestra(elementi: GanttElemento[], oggi: Date): { start: Date; end: Date } {
  const date: number[] = [oggi.getTime()];
  for (const e of elementi) {
    date.push(new Date(e.data_inizio).getTime());
    if (e.data_fine) date.push(new Date(e.data_fine).getTime());
  }
  // Le barre «in uso» non hanno fine: si estendono fino al bordo, quindi il
  // bordo deve stare almeno un mese oltre l'ultima data conosciuta.
  const haCodeAperte = elementi.some((e) => e.tipo === "barra_uso" && !e.data_fine);
  const min = new Date(Math.min(...date));
  const max = new Date(Math.max(...date));
  const end = primoDelMeseDopo(haCodeAperte ? primoDelMeseDopo(max) : max);
  return { start: primoDelMese(min), end };
}

export function Gantt({
  elementi,
  moduli,
  oggi = new Date(),
  variante = "compatto",
}: GanttProps) {
  const disteso = variante === "disteso";

  const { start, end } = useMemo(() => calcolaFinestra(elementi, oggi), [elementi, oggi]);
  const totaleGiorni = giorniTra(start, end);
  const pct = (d: Date) =>
    Math.max(0, Math.min(100, (giorniTra(start, d) / totaleGiorni) * 100));

  const oggiPct = pct(oggi);
  const moduliById = useMemo(() => new Map(moduli.map((m) => [m.id, m])), [moduli]);

  // Una corsia per etichetta: è ciò che permette a una fase libera di stare
  // accanto ai moduli senza doverne fingere uno.
  const corsie = useMemo(() => {
    const map = new Map<
      string,
      { etichetta: string; modulo?: Modulo; barre: GanttElemento[] }
    >();
    for (const el of elementi) {
      if (el.tipo !== "barra_costruzione" && el.tipo !== "barra_uso") continue;
      const esistente = map.get(el.etichetta);
      if (esistente) {
        esistente.barre.push(el);
      } else {
        map.set(el.etichetta, {
          etichetta: el.etichetta,
          modulo: el.modulo_id ? moduliById.get(el.modulo_id) : undefined,
          barre: [el],
        });
      }
    }
    return Array.from(map.values()).sort(
      (a, b) => (a.barre[0].ordine ?? 0) - (b.barre[0].ordine ?? 0)
    );
  }, [elementi, moduliById]);

  const bande = elementi.filter((e) => e.tipo === "banda_temporale");
  const milestone = elementi
    .filter((e) => e.tipo === "punto_milestone")
    .sort((a, b) => a.data_inizio.localeCompare(b.data_inizio));

  const mesi = useMemo(() => {
    const out: { label: string; anno: number; pct: number }[] = [];
    const cursore = new Date(start);
    while (cursore < end) {
      out.push({
        label: MESI_IT[cursore.getUTCMonth()],
        anno: cursore.getUTCFullYear(),
        pct: pct(cursore),
      });
      cursore.setUTCMonth(cursore.getUTCMonth() + 1);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end]);

  const larghezzaEtichette = disteso ? "220px" : "150px";

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <div
        className="grid text-sm"
        style={{ gridTemplateColumns: `${larghezzaEtichette} 1fr` }}
      >
        {/* Intestazione dei mesi */}
        <div className="border-b border-r bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
          Linea di lavoro
        </div>
        <div className={cn("relative border-b bg-muted/40", disteso ? "h-10" : "h-9")}>
          {mesi.map((m, i) => (
            <div
              key={i}
              className="absolute inset-y-0 flex items-center px-2 text-xs font-medium text-muted-foreground"
              style={{ left: `${m.pct}%` }}
            >
              {m.label}
              {/* L'anno solo a gennaio: il resto lo si deduce, e ripeterlo
                  dodici volte è rumore. */}
              {m.label === "gen" && (
                <span className="ml-1 text-[10px] opacity-60">{m.anno}</span>
              )}
            </div>
          ))}
          {/* «oggi» va nominato, non solo tracciato: una linea senza etichetta
              in mezzo ad altre linee non si distingue. */}
          <div className="absolute inset-y-0 w-px bg-primary" style={{ left: `${oggiPct}%` }}>
            <span className="absolute top-0.5 left-1 text-[10px] font-medium text-primary whitespace-nowrap">
              oggi
            </span>
          </div>
        </div>

        {/* Corsie */}
        {corsie.map((c, i) => (
          <Corsia
            key={c.etichetta}
            corsia={c}
            bande={bande}
            oggiPct={oggiPct}
            pct={pct}
            disteso={disteso}
            ultima={i === corsie.length - 1}
          />
        ))}

        {/* Le milestone: una fascia sola in fondo, non un pallino per corsia. */}
        {milestone.length > 0 && (
          <>
            <div className="px-3 py-2 border-r border-t text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center">
              Milestone
            </div>
            <div className={cn("relative border-t", disteso ? "h-12" : "h-10")}>
              {milestone.map((m) => (
                <MilestoneEtichetta
                  key={m.id}
                  elemento={m}
                  sinistra={pct(new Date(m.data_inizio))}
                  passata={new Date(m.data_inizio) < oggi}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Legenda */}
      <div className="border-t bg-muted/40 px-4 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <VoceLegenda className="bg-primary" label="costruzione" />
        <VoceLegenda className="bg-accent border border-primary/30" label="in uso" />
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-px h-3 bg-warning" />
          milestone
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 border border-dashed border-muted-foreground/60 bg-muted/80" />
          finestra esterna
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-px h-3 bg-primary" />
          oggi
        </span>
      </div>
    </div>
  );
}

function VoceLegenda({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn("inline-block w-4 h-2 rounded-sm", className)} />
      {label}
    </span>
  );
}

function MilestoneEtichetta({
  elemento,
  sinistra,
  passata,
}: {
  elemento: GanttElemento;
  sinistra: number;
  passata: boolean;
}) {
  // Oltre l'80% l'etichetta uscirebbe dal grafico: si ancora a destra.
  const aDestra = sinistra > 80;
  return (
    <div
      className="absolute inset-y-0 flex flex-col justify-center"
      style={{ left: `${sinistra}%` }}
    >
      <div
        className={cn(
          "absolute -top-px bottom-0 w-px",
          passata ? "bg-warning/40" : "bg-warning"
        )}
      />
      <span
        className={cn(
          "relative whitespace-nowrap text-[10px] font-medium px-1.5 py-0.5 rounded",
          aDestra ? "-translate-x-full -ml-1" : "ml-1",
          passata
            ? "text-muted-foreground bg-muted"
            : "text-warning bg-warning/10 border border-warning/30"
        )}
        title={`${elemento.etichetta} · ${elemento.data_inizio}${
          elemento.descrizione ? ` — ${elemento.descrizione}` : ""
        }`}
      >
        {elemento.etichetta}
      </span>
    </div>
  );
}

function Corsia({
  corsia,
  bande,
  oggiPct,
  pct,
  disteso,
  ultima,
}: {
  corsia: { etichetta: string; modulo?: Modulo; barre: GanttElemento[] };
  bande: GanttElemento[];
  oggiPct: number;
  pct: (d: Date) => number;
  disteso: boolean;
  ultima: boolean;
}) {
  const bordo = ultima ? "" : "border-b";

  return (
    <>
      <div className={cn("px-3 py-3 border-r flex items-center gap-2 min-w-0", bordo)}>
        {corsia.modulo?.codice && (
          <span className="font-mono text-[10px] text-muted-foreground shrink-0">
            {corsia.modulo.codice}
          </span>
        )}
        <span className="text-xs font-medium leading-tight">{corsia.etichetta}</span>
      </div>

      <div className={cn("relative", disteso ? "h-14" : "h-12", bordo)}>
        {/* Finestre esterne (fiera, ferie): sfondo */}
        {bande.map((b) => {
          if (!b.data_fine) return null;
          const sx = pct(new Date(b.data_inizio));
          const dx = pct(new Date(b.data_fine));
          return (
            <div
              key={b.id}
              className="absolute inset-y-0 bg-muted/60 border-l border-r border-dashed border-muted-foreground/30"
              style={{ left: `${sx}%`, width: `${dx - sx}%` }}
              title={b.etichetta}
            />
          );
        })}

        {/* Costruzione sopra, «in uso» sotto: **impilate, non sovrapposte**.
            Da quando la roadmap dice il vero le due fasi convivono — un modulo
            si continua a costruire mentre è già in uso — e disegnandole sulla
            stessa riga la seconda copriva la prima, facendo sembrare la
            costruzione molto più corta di quanto sia stata. */}
        {corsia.barre.map((barra) => {
          const sx = pct(new Date(barra.data_inizio));
          const dx = barra.data_fine ? pct(new Date(barra.data_fine)) : 100;
          const costruzione = barra.tipo === "barra_costruzione";
          return (
            <div
              key={barra.id}
              className={cn(
                "absolute h-2.5 rounded-sm",
                costruzione
                  ? cn("bg-primary", disteso ? "top-3.5" : "top-2.5")
                  : cn("bg-accent border border-primary/30", disteso ? "top-7" : "top-6")
              )}
              style={{ left: `${sx}%`, width: `${Math.max(0.5, dx - sx)}%` }}
              title={`${barra.etichetta} · ${costruzione ? "costruzione" : "in uso"} · ${
                barra.data_inizio
              }${barra.data_fine ? ` → ${barra.data_fine}` : " →"}`}
            />
          );
        })}

        <div
          className="absolute inset-y-0 w-px bg-primary pointer-events-none"
          style={{ left: `${oggiPct}%` }}
        />
      </div>
    </>
  );
}
